<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="reward-lightbox"
      :class="{ 'reward-lightbox-dark': isDarkMode }"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @click="close"
    >
      <button
        type="button"
        class="inga-close-btn reward-lightbox-close"
        :class="{ 'inga-close-btn-dark': isDarkMode }"
        aria-label="Zavřít"
        @click="close"
      >&times;</button>
      <button
        v-if="hasMany"
        type="button"
        class="reward-lightbox-nav is-prev"
        aria-label="Předchozí obrázek"
        @click.stop="step(-1)"
      >‹</button>
      <img
        class="reward-lightbox-img"
        :src="src"
        :alt="alt"
        @click.stop
      >
      <button
        v-if="hasMany"
        type="button"
        class="reward-lightbox-nav is-next"
        aria-label="Další obrázek"
        @click.stop="step(1)"
      >›</button>
      <div v-if="hasMany" class="reward-lightbox-count" @click.stop>
        {{ index + 1 }} / {{ sources.length }}
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, watch, onUnmounted } from "vue";
import { useStore } from "vuex";

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const isOpen = computed(() => !!store.state.lightbox?.isOpen);
const src = computed(() => store.state.lightbox?.src || "");
const alt = computed(() => store.state.lightbox?.alt || "Výhra");
const sources = computed(() => (
  Array.isArray(store.state.lightbox?.sources) ? store.state.lightbox.sources : []
));
const index = computed(() => Number(store.state.lightbox?.index) || 0);
const hasMany = computed(() => sources.value.length > 1);

const close = () => store.dispatch("closeLightbox");
const step = (delta) => store.dispatch("stepLightbox", delta);

const onKey = (event) => {
  if (!isOpen.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    close();
    return;
  }
  if (!hasMany.value) return;
  if (event.key === "ArrowLeft") {
    event.preventDefault();
    step(-1);
  } else if (event.key === "ArrowRight") {
    event.preventDefault();
    step(1);
  }
};

watch(isOpen, (open) => {
  if (typeof window === "undefined") return;
  if (open) {
    window.addEventListener("keydown", onKey, true);
  } else {
    window.removeEventListener("keydown", onKey, true);
  }
}, { immediate: true });

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", onKey, true);
  }
});
</script>

<style scoped>
.reward-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.82);
  cursor: zoom-out;
}

.reward-lightbox-dark {
  background: rgba(2, 6, 23, 0.88);
}

.reward-lightbox-img {
  max-width: min(910px, 96vw);
  max-height: 92vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 0.65rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
  cursor: default;
}

.reward-lightbox-close {
  position: absolute;
  top: 0.85rem;
  right: 1.1rem;
}

.reward-lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 2.8rem;
  height: 2.8rem;
  border: 0;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-lightbox-nav:hover {
  background: rgba(255, 138, 76, 0.92);
}

.reward-lightbox-nav.is-prev {
  left: 0.85rem;
}

.reward-lightbox-nav.is-next {
  right: 0.85rem;
}

.reward-lightbox-count {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: rgba(15, 23, 42, 0.7);
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  cursor: default;
}
</style>
